import { Link } from "react-router-dom";
import React, { createContext, useContext, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TrustBadges } from "@/components/TrustBadges";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Layers, Music2, Loader2, Check, FileText } from "lucide-react";

const PurchaseContext = createContext(null);
export const usePurchase = () => useContext(PurchaseContext);

function BuyDialog({ track, onClose, onConfirm, loading }) {
  const [agreed, setAgreed] = useState(false);
  if (!track) return null;

  const producer = track.producer || "Co Mann";

  return (
    <Dialog open={!!track} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] text-[#f3f4f6] sm:max-w-md max-h-[90dvh] overflow-y-auto" data-testid="buy-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight">Buy “{track.title}”</DialogTitle>
          <DialogDescription className="text-[#9ca3af]">
            Exclusive rights — once you buy this beat, it's yours alone and removed from the store.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-[#2a2a2a] p-4 mt-4 space-y-2.5" data-testid="buy-included">
          <p className="text-xs font-mono uppercase tracking-widest text-[#6b7280]">What's included</p>
          <div className="flex items-center gap-2 text-sm"><Music2 className="w-4 h-4 text-[#e2ff31]" /> High-quality {track.audio_format || "MP3"} master</div>
          {(track.stems_available || track.wav_available) && (
            <div className="flex items-center gap-2 text-sm" data-testid="buy-included-extras">
              <Layers className="w-4 h-4 text-[#e2ff31]" /> {track.stems_available ? "WAV master + stems (ZIP) — included" : "WAV master — included"}
            </div>
          )}
          <div className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-[#e2ff31]" /> Instant download after payment</div>
        </div>

        <div className="mt-4">
          <p className="text-xs font-mono uppercase tracking-widest text-[#6b7280] flex items-center gap-2 mb-2">
            <FileText className="w-3.5 h-3.5" /> License Terms &amp; Agreement
          </p>
          <ScrollArea data-testid="terms-scroll" className="h-40 rounded-xl border border-[#2a2a2a] bg-[#121212] p-3">
            <div className="text-xs leading-relaxed text-[#9ca3af] space-y-2 pr-2">
              <p className="text-[#f3f4f6] font-semibold">Co Mann Beats — Exclusive License Agreement</p>
              <p>By completing this purchase you (“Licensee”) agree to the following terms with Co Mann Beats (“Licensor”):</p>
              <p><strong className="text-[#f3f4f6]">1. Grant of Rights.</strong> Upon full payment, Licensee receives exclusive rights to record, reproduce, distribute, perform, and monetize songs created with this beat across all formats and platforms worldwide.</p>
              <p><strong className="text-[#f3f4f6]">2. Exclusivity.</strong> Once sold, this beat is removed from the store and will not be re-sold or re-licensed to anyone else. Licensor retains the right to use it for portfolio and promotional purposes.</p>
              <p><strong className="text-[#f3f4f6]">3. Ownership.</strong> The underlying instrumental composition remains the intellectual property of Co Mann Beats. Licensee owns the resulting song (their vocals combined with the beat).</p>
              <p><strong className="text-[#f3f4f6]">4. Producer Credit.</strong> Where practical, Licensee agrees to credit “Prod. by {producer}” in song titles, metadata, and descriptions.</p>
              <p><strong className="text-[#f3f4f6]">5. Delivery &amp; Refunds.</strong> Files (MP3, plus WAV/stems where included) are delivered digitally and immediately after payment. Because this is a digital, exclusive product, <strong className="text-[#f3f4f6]">all sales are final and non-refundable</strong>, except for a duplicate charge, files that can't be delivered or fixed within 3 days, or an unauthorized charge, reported within 7 days of purchase as described in the <Link to="/refund-policy" target="_blank" className="text-[#e2ff31] underline">Refund Policy</Link>.</p>
              <p><strong className="text-[#f3f4f6]">6. Restrictions.</strong> Licensee may not resell, redistribute, or license the beat as a standalone instrumental, nor register the underlying instrumental with any Content-ID or publishing system as their own original work.</p>
              <p><strong className="text-[#f3f4f6]">7. Liability.</strong> The beat is provided “as is.” Licensor is not liable for any damages arising from its use.</p>
            </div>
          </ScrollArea>
          <label htmlFor="terms-agree" className="mt-3 flex items-start gap-2.5 cursor-pointer select-none">
            <Checkbox id="terms-agree" data-testid="terms-agree-checkbox" checked={agreed}
              onCheckedChange={(v) => setAgreed(!!v)}
              className="mt-0.5 border-[#2a2a2a] data-[state=checked]:bg-[#e2ff31] data-[state=checked]:text-black data-[state=checked]:border-[#e2ff31]" />
            <span className="text-xs text-[#9ca3af] leading-relaxed">
              I have read and agree to the Exclusive Beat License Agreement above and the{" "}
              <Link to="/refund-policy" target="_blank" data-testid="buy-refund-policy-link" onClick={(e) => e.stopPropagation()} className="text-[#e2ff31] underline">No-Refund Policy</Link>,
              and understand this purchase is final.
            </span>
          </label>
        </div>

        <button data-testid="buy-confirm-btn" disabled={loading || !agreed} onClick={() => onConfirm(track, track.stems_available)}
          className="mt-4 w-full flex items-center justify-center gap-2 rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] font-semibold py-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (agreed ? `Checkout — $${track.price.toFixed(2)}` : "Agree to the terms to continue")}
        </button>
        <TrustBadges compact className="mt-4 pt-4 border-t border-[#2a2a2a]" />
      </DialogContent>
    </Dialog>
  );
}

export const PurchaseProvider = ({ children }) => {
  const { user, setAuthOpen } = useAuth();
  const [dialogTrack, setDialogTrack] = useState(null);
  const [loadingId, setLoadingId] = useState(null);

  const startCheckout = async (track, includeStems) => {
    setLoadingId(track.id);
    try {
      const { data } = await api.post("/checkout/session", {
        track_id: track.id, origin_url: window.location.origin, include_stems: includeStems,
        early_key: track.early_key || "",
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start checkout");
      setLoadingId(null);
      setDialogTrack(null);
    }
  };

  const openBuy = (track) => {
    if (track.sold) { toast.error("This beat has already been sold."); return; }
    if (!user) { setAuthOpen(true); return; }
    setDialogTrack(track);
  };

  return (
    <PurchaseContext.Provider value={{ openBuy, loadingId }}>
      {children}
      <BuyDialog key={dialogTrack?.id} track={dialogTrack} onClose={() => setDialogTrack(null)} onConfirm={startCheckout}
        loading={loadingId === dialogTrack?.id} />
    </PurchaseContext.Provider>
  );
};
